import { forwardRef, useEffect, useRef, useState } from 'react';
import dayjs from 'dayjs';
import useControllableState from '../hooks/useControllableState';
import useMergedRef from '../hooks/useMergeRef';
import capitalize from '../utils/capitalize';
import TimeInput from '../TimeInput';
import Calendar from './Calendar';
import BasePicker from './BasePicker';
import Button from '../Buttons';
import { useConfig } from '../ConfigProvider';

const DEFAULT_INPUT_FORMAT = 'DD-MMM-YYYY hh:mm a';

/**
 * DateTimepicker component for selecting a date and time.
 * @param {Object} props - The component props
 * @param {boolean} [props.amPm] - The flag to enable AM/PM
 * @param {string} [props.className] - The component class name
 * @param {boolean} [props.clearable] - The flag to enable the clear button
 * @param {string} [props.clearButtonLabel] - The clear button label
 * @param {boolean} [props.closePickerOnChange] - The flag to close the picker on change
 * @param {number} [props.dateViewCount] - The number of date views
 * @param {function} [props.dayClassName] - The function to add a class to the day
 * @param {object} [props.dayStyle] - The style to add to the day
 * @param {Date} [props.defaultMonth] - The default month
 * @param {boolean} [props.defaultOpen] - The flag to open the picker by default
 * @param {Date} [props.defaultValue] - The default value
 * @param {string} [props.defaultView] - The default view
 * @param {boolean} [props.disabled] - The flag to disable the picker
 * @param {function} [props.disableDate] - The function to disable the date
 * @param {boolean} [props.enableHeaderLabel] - The flag to enable the header label
 * @param {boolean} [props.disableOutOfMonth] - The flag to disable out of month dates
 * @param {string} [props.firstDayOfWeek] - The first day of the week
 * @param {boolean} [props.hideOutOfMonthDates] - The flag to hide out of month dates
 * @param {boolean} [props.hideWeekdays] - The flag to hide weekdays
 * @param {string} [props.inputFormat] - The input format
 * @param {string} [props.inputPrefix] - The input prefix
 * @param {string} [props.inputSuffix] - The input suffix
 * @param {boolean} [props.inputtable] - The flag to enable input
 * @param {object} [props.labelFormat] - The label format
 * @param {string} [props.locale] - The locale
 * @param {Date} [props.maxDate] - The maximum date
 * @param {Date} [props.minDate] - The minimum date
 * @param {string} [props.name] - The name of the picker
 * @param {string} [props.okButtonContent] - The content of the OK button
 * @param {function} [props.onBlur] - The function to call when the input loses focus
 * @param {function} [props.onChange] - The function to call when the value changes
 * @param {function} [props.onFocus] - The function to call when the input gains focus
 * @param {function} [props.onDropdownClose] - The function to call when the dropdown closes
 * @param {function} [props.onDropdownOpen] - The function to call when the dropdown opens
 * @param {boolean} [props.openPickerOnClear] - The flag to open the picker on clear
 * @param {function} [props.renderDay] - The function to render the day
 * @param {string} [props.size] - The size of the picker
 * @param {object} [props.style] - The style of the picker
 * @param {Date} [props.value] - The value
 * @param {string[]} [props.weekendDays] - The weekend days
 * @param {object} [props.yearLabelFormat] - The year label format
 * @returns {React.ReactElement}
 */
const DateTimepicker = forwardRef((props, ref) => {
    const {
        amPm,
        className,
        clearable,
        clearButtonLabel,
        closePickerOnChange,
        dateViewCount,
        dayClassName,
        dayStyle,
        defaultMonth,
        defaultOpen,
        defaultValue,
        defaultView,
        disabled,
        disableDate,
        enableHeaderLabel,
        disableOutOfMonth,
        firstDayOfWeek,
        hideOutOfMonthDates,
        hideWeekdays,
        inputFormat,
        inputPrefix,
        inputSuffix,
        inputtable,
        labelFormat,
        locale,
        maxDate,
        minDate,
        name,
        okButtonContent,
        onBlur,
        onChange,
        onFocus,
        onDropdownClose,
        onDropdownOpen,
        openPickerOnClear,
        renderDay,
        size,
        style,
        value,
        weekendDays,
        yearLabelFormat,
        ...rest
    } = props;

    const { locale: themeLocale } = useConfig();

    const finalLocale = locale || themeLocale;

    const dateFormat = inputFormat || DEFAULT_INPUT_FORMAT;

    const [dropdownOpened, setDropdownOpened] = useState(defaultOpen);

    const inputRef = useRef();

    // eslint-disable-next-line no-unused-vars
    const [_, setLastValidValue] = useState(defaultValue ?? null);
    const [_value, setValue] = useControllableState({
        prop: value,
        defaultProp: defaultValue,
        onChange,
    });

    const [calendarMonth, setCalendarMonth] = useState(
        _value || defaultMonth || new Date()
    );

    const [focused, setFocused] = useState(false);
    const [inputState, setInputState] = useState(
        _value instanceof Date
            ? capitalize(dayjs(_value).locale(finalLocale).format(dateFormat))
            : ''
    );

    const closeDropdown = () => {
        setDropdownOpened(false);
        onDropdownClose?.();
    };

    const openDropdown = () => {
        setDropdownOpened(true);
        onDropdownOpen?.();
    };

    useEffect(() => {
        if (value === null && !focused) {
            setInputState('');
        }

        if (value instanceof Date && !focused) {
            setInputState(dayjs(value).locale(finalLocale).format(dateFormat));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value, focused]);

    const handleValueChange = (date) => {
        if (_value) {
            date.setHours(_value.getHours());
            date.setMinutes(_value.getMinutes());
        } else {
            const now = new Date(Date.now());
            date.setHours(now.getHours());
            date.setMinutes(now.getMinutes());
        }
        setValue(date);
        if (!value && !closePickerOnChange) {
            setInputState(dayjs(date).locale(finalLocale).format(dateFormat));
        }
        closePickerOnChange &&
            setInputState(
                capitalize(dayjs(date).locale(finalLocale).format(dateFormat))
            );
        closePickerOnChange && closeDropdown();
        window.setTimeout(() => inputRef.current?.focus(), 0);
    };

    const handleClear = () => {
        setValue(null);
        setLastValidValue(null);
        setInputState('');
        openPickerOnClear && openDropdown();
        inputRef.current?.focus();
        onChange?.(null);
    };

    const parseDate = (date) => dayjs(date, dateFormat, finalLocale).toDate();

    const handleInputBlur = (e) => {
        typeof onBlur === 'function' && onBlur(e);
        setFocused(false);
    };

    const handleInputFocus = (e) => {
        typeof onFocus === 'function' && onFocus(e);
        setFocused(true);
    };

    const handleChange = (e) => {
        openDropdown();

        const date = parseDate(e.target.value);
        if (dayjs(date).isValid()) {
            setValue(date);
            setLastValidValue(date);
            closePickerOnChange && setInputState(e.target.value);
            setCalendarMonth(date);
        } else {
            closePickerOnChange && setInputState(e.target.value);
        }
    };

    const handleTimeChange = (time) => {
        const newDateTime = new Date(
            _value.getFullYear(),
            _value.getMonth(),
            _value.getDate(),
            time.getHours(),
            time.getMinutes(),
            time.getSeconds(),
            time.getMilliseconds()
        );
        setValue(newDateTime);

        if (!value && !closePickerOnChange) {
            setInputState(
                capitalize(
                    dayjs(newDateTime).locale(finalLocale).format(dateFormat)
                )
            );
        }

        closePickerOnChange &&
            setInputState(
                capitalize(
                    dayjs(newDateTime).locale(finalLocale).format(dateFormat)
                )
            );
        closePickerOnChange && closeDropdown();
    };

    const handleOk = () => {
        setInputState(
            capitalize(dayjs(_value).locale(finalLocale).format(dateFormat))
        );
        closeDropdown();
        window.setTimeout(() => inputRef.current?.focus(), 0);
        onChange?.(_value);
    };

    return (
        <BasePicker
            dropdownOpened={dropdownOpened}
            setDropdownOpened={setDropdownOpened}
            ref={useMergedRef(ref, inputRef)}
            className={className}
            onChange={handleChange}
            onBlur={handleInputBlur}
            onFocus={handleInputFocus}
            name={name}
            inputLabel={inputState}
            clearable={clearable && !!_value && !disabled}
            clearButtonLabel={clearButtonLabel}
            onClear={handleClear}
            disabled={disabled}
            size={size}
            inputPrefix={inputPrefix}
            inputSuffix={inputSuffix}
            {...rest}
        >
            <Calendar
                locale={finalLocale}
                month={inputtable ? calendarMonth : undefined}
                defaultMonth={
                    defaultMonth ||
                    (_value instanceof Date ? _value : new Date())
                }
                onMonthChange={setCalendarMonth}
                value={
                    _value instanceof Date
                        ? _value
                        : _value && dayjs(_value).toDate()
                }
                onChange={handleValueChange}
                labelFormat={labelFormat}
                dayClassName={dayClassName}
                dayStyle={dayStyle}
                disableOutOfMonth={disableOutOfMonth}
                minDate={minDate}
                maxDate={maxDate}
                disableDate={disableDate}
                firstDayOfWeek={firstDayOfWeek}
                preventFocus={false}
                dateViewCount={dateViewCount}
                enableHeaderLabel={enableHeaderLabel}
                defaultView={defaultView}
                hideOutOfMonthDates={hideOutOfMonthDates}
                hideWeekdays={hideWeekdays}
                renderDay={renderDay}
                weekendDays={weekendDays}
                yearLabelFormat={yearLabelFormat}
            />
            <div className="flex items-center gap-4 mt-4">
                <TimeInput
                    disabled={!_value}
                    value={_value}
                    onChange={handleTimeChange}
                    format={amPm ? '12' : '24'}
                    clearable={false}
                    size="sm"
                />
                <Button size="sm" disabled={!_value} onClick={handleOk}>
                    {okButtonContent}
                </Button>
            </div>
        </BasePicker>
    );
});

DateTimepicker.defaultProps = {
    closePickerOnChange: false,
    labelFormat: {
        month: 'MMM',
        year: 'YYYY',
    },
    defaultOpen: false,
    name: 'dateTime',
    clearable: true,
    disabled: false,
    firstDayOfWeek: 'monday',
    okButtonContent: 'OK',
    amPm: true,
};

export default DateTimepicker;
